import React, { useCallback, useEffect, useMemo, useState, useRef, Fragment } from 'react';
import { motion, useSpring, useTransform, AnimatePresence } from 'framer-motion';
import { CalculatorIcon, TrendingUpIcon, WalletIcon, StarIcon, ArrowRightIcon, ZapIcon, ShieldCheckIcon, GiftIcon, AlertTriangleIcon, ArrowUpRightIcon } from 'lucide-react';
import { Card } from '../ui/Card';
import { TIER_CONFIGS } from '../../lib/mockData';
import { formatVND, formatVNDCompact } from '../../lib/formatters';
interface MonthResult {
  month: number;
  tier: string;
  tierNameVi: string;
  tierIcon: string;
  tierColor: string;
  cumulativePoints: number;
  pointsEarned: number;
  depositBonus: number;
  pointsCreditValue: number;
  cumulativeSavings: number;
  upgraded: boolean;
}
function simulateYear(monthlySpend: number): {
  months: MonthResult[];
  totalDepositBonus: number;
  totalPointsCreditValue: number;
  totalSavings: number;
  totalPoints: number;
  finalTier: (typeof TIER_CONFIGS)[number];
  tierUpgrades: {
    month: number;
    tier: (typeof TIER_CONFIGS)[number];
  }[];
} {
  let cumulativePoints = 0;
  let currentTierIndex = 0;
  const months: MonthResult[] = [];
  const tierUpgrades: {
    month: number;
    tier: (typeof TIER_CONFIGS)[number];
  }[] = [];
  let totalDepositBonus = 0;
  let totalPointsCreditValue = 0;
  let totalPoints = 0;
  let cumulativeSavings = 0;
  for (let m = 1; m <= 12; m++) {
    const tier = TIER_CONFIGS[currentTierIndex];
    const pointsThisMonth = Math.floor(monthlySpend / 100000 * tier.pointMultiplier);
    cumulativePoints += pointsThisMonth;
    totalPoints += pointsThisMonth;
    const depositBonus = Math.floor(monthlySpend * 0.025);
    const pointsCreditValue = pointsThisMonth * 500;
    totalDepositBonus += depositBonus;
    totalPointsCreditValue += pointsCreditValue;
    cumulativeSavings += depositBonus + pointsCreditValue;
    let upgraded = false;
    for (let t = TIER_CONFIGS.length - 1; t > currentTierIndex; t--) {
      if (cumulativePoints >= TIER_CONFIGS[t].minPoints) {
        currentTierIndex = t;
        upgraded = true;
        tierUpgrades.push({
          month: m,
          tier: TIER_CONFIGS[t]
        });
        break;
      }
    }
    const currentTier = TIER_CONFIGS[currentTierIndex];
    months.push({
      month: m,
      tier: currentTier.level,
      tierNameVi: currentTier.nameVi,
      tierIcon: currentTier.icon,
      tierColor: currentTier.color,
      cumulativePoints,
      pointsEarned: pointsThisMonth,
      depositBonus,
      pointsCreditValue,
      cumulativeSavings,
      upgraded
    });
  }
  return {
    months,
    totalDepositBonus,
    totalPointsCreditValue,
    totalSavings: totalDepositBonus + totalPointsCreditValue,
    totalPoints,
    finalTier: TIER_CONFIGS[currentTierIndex],
    tierUpgrades
  };
}
// Animated number that shows FULL zeros (e.g., 21.600.000đ)
function AnimatedFullNumber({
  value,
  className = ''
}: {
  value: number;
  className?: string;
}) {
  const spring = useSpring(0, {
    stiffness: 40,
    damping: 18
  });
  const display = useTransform(spring, v => {
    const num = Math.round(v);
    if (num <= 0) return '0đ';
    return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.') + 'đ';
  });
  useEffect(() => {
    spring.set(value);
  }, [value, spring]);
  return <motion.span className={className} data-id="element-272">{display}</motion.span>;
}
function AnimatedCompactNumber({
  value,
  prefix = '',
  suffix = ''
}: {
  value: number;
  prefix?: string;
  suffix?: string;
}) {
  const spring = useSpring(0, {
    stiffness: 60,
    damping: 20
  });
  const display = useTransform(spring, v => {
    const num = Math.round(v);
    if (num >= 1000000000) return `${prefix}${(num / 1000000000).toFixed(1).replace('.0', '')} tỷ${suffix}`;
    if (num >= 1000000) return `${prefix}${(num / 1000000).toFixed(1).replace('.0', '')}M${suffix}`;
    if (num >= 1000) return `${prefix}${Math.round(num / 1000)}K${suffix}`;
    return `${prefix}${num.toLocaleString('vi-VN')}${suffix}`;
  });
  useEffect(() => {
    spring.set(value);
  }, [value, spring]);
  return <motion.span data-id="element-273">{display}</motion.span>;
}
function AnimatedPoints({
  value
}: {
  value: number;
}) {
  const spring = useSpring(0, {
    stiffness: 60,
    damping: 20
  });
  const display = useTransform(spring, v => `${Math.round(v).toLocaleString('vi-VN')}`);
  useEffect(() => {
    spring.set(value);
  }, [value, spring]);
  return <motion.span data-id="element-274">{display}</motion.span>;
}
// SVG Cumulative Growth Chart
function CumulativeChart({
  months,
  tierUpgrades
}: {
  months: MonthResult[];
  tierUpgrades: {
    month: number;
    tier: (typeof TIER_CONFIGS)[number];
  }[];
}) {
  const maxSavings = months[months.length - 1]?.cumulativeSavings || 1;
  const chartW = 100;
  const chartH = 50;
  const padL = 0;
  const padR = 0;
  const padT = 4;
  const padB = 2;
  const w = chartW - padL - padR;
  const h = chartH - padT - padB;
  const points = months.map((m, i) => {
    const x = padL + i / (months.length - 1) * w;
    const y = padT + h - m.cumulativeSavings / maxSavings * h;
    return {
      x,
      y,
      month: m
    };
  });
  const linePath = points.map((p, i) => `${i === 0 ? 'M' : 'L'}${p.x},${p.y}`).join(' ');
  const areaPath = `${linePath} L${points[points.length - 1].x},${padT + h} L${points[0].x},${padT + h} Z`;
  return <div className="relative" data-id="element-275">
      <svg viewBox={`0 0 ${chartW} ${chartH + 8}`} className="w-full h-auto" preserveAspectRatio="none" data-id="element-276">
        <defs data-id="element-277">
          <linearGradient id="savingsGrad" x1="0" y1="0" x2="0" y2="1" data-id="element-278">
            <stop offset="0%" stopColor="#22C55E" stopOpacity="0.3" data-id="element-279" />
            <stop offset="100%" stopColor="#22C55E" stopOpacity="0.02" data-id="element-280" />
          </linearGradient>
        </defs>
        {/* Grid lines */}
        {[0.25, 0.5, 0.75].map(frac => <line key={frac} x1={padL} y1={padT + h * (1 - frac)} x2={padL + w} y2={padT + h * (1 - frac)} stroke="#2A2A2A" strokeWidth="0.15" data-id="element-281" />)}
        {/* Area fill */}
        <motion.path d={areaPath} fill="url(#savingsGrad)" initial={{
        opacity: 0
      }} whileInView={{
        opacity: 1
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.8,
        delay: 0.3
      }} data-id="element-282" />
        {/* Line */}
        <motion.path d={linePath} fill="none" stroke="#22C55E" strokeWidth="0.6" strokeLinecap="round" strokeLinejoin="round" initial={{
        pathLength: 0
      }} whileInView={{
        pathLength: 1
      }} viewport={{
        once: true
      }} transition={{
        duration: 1.5,
        ease: 'easeOut'
      }} data-id="element-283" />
        {/* Tier upgrade markers */}
        {tierUpgrades.map(u => {
        const pt = points[u.month - 1];
        if (!pt) return null;
        return <g key={u.tier.level} data-id="element-284">
              <circle cx={pt.x} cy={pt.y} r="1.2" fill={u.tier.color} opacity="0.8" data-id="element-285" />
              <line x1={pt.x} y1={pt.y + 1.5} x2={pt.x} y2={padT + h} stroke={u.tier.color} strokeWidth="0.15" strokeDasharray="0.5,0.5" opacity="0.4" data-id="element-286" />
            </g>;
      })}
        {/* End point glow */}
        {points.length > 0 && <motion.circle cx={points[points.length - 1].x} cy={points[points.length - 1].y} r="1.5" fill="#22C55E" initial={{
        scale: 0
      }} whileInView={{
        scale: 1
      }} viewport={{
        once: true
      }} transition={{
        delay: 1.5,
        type: 'spring'
      }} data-id="element-287" />}
        {/* X-axis labels */}
        {months.map((m, i) => {
        const x = padL + i / (months.length - 1) * w;
        return <text key={i} x={x} y={chartH + 6} textAnchor="middle" fill="#666666" fontSize="2.2" fontFamily="system-ui" data-id="element-288">
              T{m.month}
            </text>;
      })}
      </svg>
      {/* Tier upgrade labels */}
      <div className="absolute inset-0 pointer-events-none" data-id="element-289">
        {tierUpgrades.map(u => {
        const leftPct = (u.month - 1) / (months.length - 1) * 100;
        return <motion.div key={u.tier.level} className="absolute text-[9px] font-semibold px-1 py-0.5 rounded" style={{
          left: `${Math.min(leftPct, 88)}%`,
          top: '2px',
          color: u.tier.color,
          backgroundColor: `${u.tier.color}15`
        }} initial={{
          opacity: 0,
          y: 4
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: 1 + tierUpgrades.indexOf(u) * 0.2
        }} data-id="element-290">
              {u.tier.icon} {u.tier.nameVi}
            </motion.div>;
      })}
      </div>
    </div>;
}
// Stacked Bar Chart
function StackedBarChart({
  months
}: {
  months: MonthResult[];
}) {
  const maxValue = Math.max(...months.map(m => m.depositBonus + m.pointsCreditValue));
  const [hoveredMonth, setHoveredMonth] = useState<number | null>(null);
  return <div className="relative" data-id="element-291">
      <div className="flex items-end gap-1.5 h-32" data-id="element-292">
        {months.map((m, i) => {
        const total = m.depositBonus + m.pointsCreditValue;
        const totalPct = maxValue > 0 ? total / maxValue * 100 : 10;
        const depositPct = total > 0 ? m.depositBonus / total * 100 : 50;
        const pointsPct = 100 - depositPct;
        return <motion.div key={i} className="flex-1 flex flex-col items-center gap-1 relative" initial={{
          opacity: 0,
          scaleY: 0
        }} whileInView={{
          opacity: 1,
          scaleY: 1
        }} viewport={{
          once: true
        }} transition={{
          delay: 0.04 * i,
          duration: 0.4
        }} style={{
          originY: 1
        }} onMouseEnter={() => setHoveredMonth(i)} onMouseLeave={() => setHoveredMonth(null)} data-id="element-293">
              {/* Tooltip */}
              <AnimatePresence data-id="element-294">
                {hoveredMonth === i && <motion.div initial={{
              opacity: 0,
              y: 4
            }} animate={{
              opacity: 1,
              y: 0
            }} exit={{
              opacity: 0,
              y: 4
            }} className="absolute -top-16 left-1/2 -translate-x-1/2 bg-[#0D0D0D] border border-[#2A2A2A] rounded-lg px-2.5 py-1.5 z-20 whitespace-nowrap shadow-xl" data-id="element-295">
                    <p className="text-[10px] text-white font-semibold mb-0.5" data-id="element-296">
                      Tháng {m.month}
                    </p>
                    <p className="text-[9px] text-[#C8A84E]" data-id="element-297">
                      +2.5% thưởng: {formatVND(m.depositBonus)}
                    </p>
                    <p className="text-[9px] text-[#22C55E]" data-id="element-298">
                      Điểm: {formatVND(m.pointsCreditValue)}
                    </p>
                    <div className="absolute bottom-0 left-1/2 -translate-x-1/2 translate-y-full" data-id="element-299">
                      <div className="w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-[#2A2A2A]" data-id="element-300" />
                    </div>
                  </motion.div>}
              </AnimatePresence>

              <div className="w-full rounded-t-sm overflow-hidden cursor-pointer transition-all duration-200" style={{
            height: `${Math.max(totalPct, 8)}%`,
            boxShadow: m.upgraded ? `0 0 8px ${m.tierColor}40` : 'none',
            opacity: hoveredMonth !== null && hoveredMonth !== i ? 0.4 : 1
          }} data-id="element-301">
                {/* Points credit portion (top - green) */}
                <div className="w-full transition-colors" style={{
              height: `${pointsPct}%`,
              backgroundColor: hoveredMonth === i ? '#22C55E' : '#22C55E80'
            }} data-id="element-302" />
                {/* Deposit bonus portion (bottom - gold) */}
                <div className="w-full transition-colors" style={{
              height: `${depositPct}%`,
              backgroundColor: hoveredMonth === i ? '#C8A84E' : '#C8A84Ea0'
            }} data-id="element-303" />
              </div>
              <span className={`text-[9px] transition-colors ${hoveredMonth === i ? 'text-white' : 'text-[#666666]'}`} data-id="element-304">
                T{m.month}
              </span>
            </motion.div>;
      })}
      </div>
      {/* Legend */}
      <div className="flex items-center justify-center gap-4 mt-3" data-id="element-305">
        <div className="flex items-center gap-1.5" data-id="element-306">
          <div className="w-2.5 h-2.5 rounded-sm bg-[#C8A84E]" data-id="element-307" />
          <span className="text-[10px] text-[#B0B0B0]" data-id="element-308">Tín dụng +2.5%</span>
        </div>
        <div className="flex items-center gap-1.5" data-id="element-309">
          <div className="w-2.5 h-2.5 rounded-sm bg-[#22C55E]" data-id="element-310" />
          <span className="text-[10px] text-[#B0B0B0]" data-id="element-311">Giá trị điểm</span>
        </div>
      </div>
    </div>;
}
export function SavingsCalculator() {
  const [monthlySpend, setMonthlySpend] = useState(50000000);
  const handleSliderChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setMonthlySpend(Number(e.target.value));
  }, []);
  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value.replace(/\D/g, '');
    const num = parseInt(raw, 10) || 0;
    const clamped = Math.max(10000000, Math.min(500000000, num));
    const rounded = Math.round(clamped / 10000000) * 10000000;
    setMonthlySpend(rounded);
  }, []);
  const result = useMemo(() => simulateYear(monthlySpend), [monthlySpend]);
  const annualSpend = monthlySpend * 12;
  const savingsPercent = annualSpend > 0 ? (result.totalSavings / annualSpend * 100).toFixed(1) : '0';
  const sliderPercent = (monthlySpend - 10000000) / (500000000 - 10000000) * 100;
  const monthlyLoss = result.months[0] ? result.months[0].depositBonus + result.months[0].pointsCreditValue : 0;
  return <section className="py-16 md:py-24 px-4" id="savings-calculator" data-id="element-312">
      <div className="max-w-5xl mx-auto" data-id="element-313">
        <motion.div initial={{
        opacity: 0,
        y: 24
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} className="text-center mb-10" data-id="element-314">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-[#22C55E]/10 border border-[#22C55E]/20 rounded-full mb-4" data-id="element-315">
            <CalculatorIcon className="w-4 h-4 text-[#22C55E]" data-id="element-316" />
            <span className="text-sm font-medium text-[#22C55E]" data-id="element-317">
              Công Cụ Tính Lợi Ích
            </span>
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-3" data-id="element-318">
            Bạn Nhận Được <span className="text-[#22C55E]" data-id="element-319">Bao Nhiêu</span>?
          </h2>
          <p className="text-[#B0B0B0] max-w-2xl mx-auto" data-id="element-320">
            Kéo thanh trượt để xem tổng giá trị bạn nhận được sau 12 tháng — con
            số thật, không phóng đại
          </p>
        </motion.div>

        {/* Slider + Input */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        delay: 0.1
      }} data-id="element-321">
          <Card className="mb-6 !p-6 md:!p-8" data-id="element-322">
            <div className="mb-6" data-id="element-323">
              <label className="block text-sm font-medium text-[#B0B0B0] mb-2" data-id="element-324">
                Chi tiêu hàng tháng (ước tính)
              </label>
              <div className="flex items-center gap-4 mb-4" data-id="element-325">
                <div className="relative flex-1" data-id="element-326">
                  <input type="text" value={formatVND(monthlySpend)} onChange={handleInputChange} className="w-full bg-[#141414] border border-[#2A2A2A] rounded-xl px-4 py-3 text-xl md:text-2xl font-bold text-[#C8A84E] text-center focus:outline-none focus:border-[#C8A84E] transition-colors" aria-label="Số tiền chi tiêu hàng tháng" data-id="element-327" />
                  <span className="absolute right-4 top-1/2 -translate-y-1/2 text-sm text-[#666666]" data-id="element-328">
                    /tháng
                  </span>
                </div>
              </div>
              <div className="relative mt-2 mb-2" data-id="element-329">
                <div className="relative h-3 bg-[#2A2A2A] rounded-full overflow-visible" data-id="element-330">
                  <motion.div className="absolute top-0 left-0 h-full rounded-full bg-gradient-to-r from-[#C8A84E] to-[#D4B85A]" style={{
                  width: `${sliderPercent}%`
                }} layout transition={{
                  type: 'spring',
                  stiffness: 300,
                  damping: 30
                }} data-id="element-331" />
                </div>
                <input type="range" min={10000000} max={500000000} step={10000000} value={monthlySpend} onChange={handleSliderChange} className="absolute top-0 left-0 w-full h-3 opacity-0 cursor-pointer z-10" style={{
                margin: 0
              }} aria-label="Kéo để chọn số tiền chi tiêu" data-id="element-332" />
                <motion.div className="absolute top-1/2 -translate-y-1/2 w-6 h-6 rounded-full bg-[#C8A84E] border-4 border-[#0D0D0D] shadow-lg shadow-[#C8A84E]/30 pointer-events-none" style={{
                left: `calc(${sliderPercent}% - 12px)`,
                top: '6px'
              }} layout transition={{
                type: 'spring',
                stiffness: 300,
                damping: 30
              }} data-id="element-333" />
              </div>
              <div className="flex justify-between text-xs text-[#666666] mt-1" data-id="element-334">
                <span data-id="element-335">10M/tháng</span>
                <span data-id="element-336">250M</span>
                <span data-id="element-337">500M/tháng</span>
              </div>
            </div>
          </Card>
        </motion.div>

        {/* HERO SAVINGS — FULL ZEROS */}
        <motion.div initial={{
        opacity: 0,
        scale: 0.95
      }} whileInView={{
        opacity: 1,
        scale: 1
      }} viewport={{
        once: true
      }} transition={{
        delay: 0.15
      }} className="mb-6" data-id="element-338">
          <div className="relative bg-gradient-to-br from-[#22C55E]/10 via-[#1A1A1A] to-[#22C55E]/5 border border-[#22C55E]/20 rounded-2xl p-6 md:p-10 text-center overflow-hidden" data-id="element-339">
            {/* Subtle glow */}
            <div className="absolute top-0 left-1/2 -translate-x-1/2 w-64 h-32 bg-[#22C55E]/8 rounded-full blur-3xl pointer-events-none" data-id="element-340" />

            <p className="relative text-sm text-[#22C55E] font-medium mb-2 tracking-wide uppercase" data-id="element-341">
              Tổng giá trị nhận được sau 12 tháng
            </p>
            <div className="relative text-5xl md:text-7xl lg:text-8xl font-bold text-[#22C55E] mb-3 tracking-tight" data-id="element-342">
              <AnimatedFullNumber value={result.totalSavings} data-id="element-343" />
            </div>
            <div className="relative flex items-center justify-center gap-2 text-sm text-[#B0B0B0]" data-id="element-344">
              <span data-id="element-345">Tương đương</span>
              <span className="text-[#22C55E] font-bold text-lg" data-id="element-346">
                {savingsPercent}%
              </span>
              <span data-id="element-347">tổng chi tiêu của bạn</span>
            </div>

            {/* Comparison bar */}
            <div className="relative mt-6 max-w-lg mx-auto" data-id="element-348">
              <div className="flex items-center justify-between text-xs mb-1.5" data-id="element-349">
                <span className="text-[#B0B0B0]" data-id="element-350">
                  Chi tiêu/năm:{' '}
                  <span className="text-white font-semibold" data-id="element-351">
                    {formatVNDCompact(annualSpend)}
                  </span>
                </span>
                <span className="text-[#22C55E] font-semibold flex items-center gap-1" data-id="element-352">
                  <ArrowUpRightIcon className="w-3 h-3" data-id="element-353" />
                  Nhận lại: {formatVND(result.totalSavings)}
                </span>
              </div>
              <div className="relative h-6 bg-[#2A2A2A] rounded-lg overflow-hidden" data-id="element-354">
                <motion.div className="absolute inset-y-0 left-0 bg-gradient-to-r from-[#22C55E]/40 to-[#22C55E]/15 rounded-lg" initial={{
                width: 0
              }} whileInView={{
                width: '100%'
              }} viewport={{
                once: true
              }} transition={{
                duration: 0.6
              }} data-id="element-355" />
                <motion.div className="absolute inset-y-0 right-0 bg-[#22C55E]/25 border-l-2 border-[#22C55E] rounded-r-lg flex items-center justify-center" style={{
                width: `${Math.max(parseFloat(savingsPercent), 3)}%`
              }} initial={{
                opacity: 0
              }} animate={{
                opacity: 1
              }} transition={{
                delay: 0.5
              }} data-id="element-356">
                  <span className="text-[9px] font-bold text-[#22C55E] whitespace-nowrap px-1" data-id="element-357">
                    +{savingsPercent}%
                  </span>
                </motion.div>
              </div>
            </div>
          </div>
        </motion.div>

        {/* LOSS AVERSION CALLOUT */}
        <motion.div initial={{
        opacity: 0,
        y: 12
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        delay: 0.2
      }} className="mb-6" data-id="element-358">
          <div className="bg-[#EF4444]/5 border border-[#EF4444]/20 rounded-xl p-4 md:p-5 flex items-start gap-3" data-id="element-359">
            <div className="w-10 h-10 bg-[#EF4444]/10 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5" data-id="element-360">
              <AlertTriangleIcon className="w-5 h-5 text-[#EF4444]" data-id="element-361" />
            </div>
            <div data-id="element-362">
              <p className="text-sm font-semibold text-[#EF4444] mb-0.5" data-id="element-363">
                Mỗi tháng chưa tham gia, bạn đang mất:
              </p>
              <motion.p className="text-2xl md:text-3xl font-bold text-[#EF4444]" animate={{
              opacity: [1, 0.6, 1]
            }} transition={{
              duration: 2,
              repeat: Infinity,
              ease: 'easeInOut'
            }} data-id="element-364">
                -{formatVND(monthlyLoss)}
              </motion.p>
              <p className="text-xs text-[#B0B0B0] mt-1" data-id="element-365">
                Đó là tiền bạn{' '}
                <span className="text-[#EF4444] font-medium" data-id="element-366">
                  đã có thể nhận
                </span>{' '}
                nếu đăng ký hôm nay. Sau 12 tháng = mất{' '}
                {formatVND(result.totalSavings)}.
              </p>
            </div>
          </div>
        </motion.div>

        {/* Breakdown Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4 mb-8" data-id="element-367">
          <motion.div initial={{
          opacity: 0,
          y: 16
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: 0.1
        }} data-id="element-368">
            <Card className="text-center !p-4 md:!p-5" data-id="element-369">
              <div className="w-9 h-9 bg-[#C8A84E]/10 rounded-xl flex items-center justify-center mx-auto mb-2" data-id="element-370">
                <WalletIcon className="w-4 h-4 text-[#C8A84E]" data-id="element-371" />
              </div>
              <p className="text-[10px] md:text-xs text-[#666666] mb-1" data-id="element-372">
                Tín dụng thưởng +2.5%
              </p>
              <div className="text-lg md:text-xl font-bold text-[#22C55E]" data-id="element-373">
                +<AnimatedCompactNumber value={result.totalDepositBonus} data-id="element-374" />
              </div>
            </Card>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          y: 16
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: 0.15
        }} data-id="element-375">
            <Card className="text-center !p-4 md:!p-5" data-id="element-376">
              <div className="w-9 h-9 bg-[#C8A84E]/10 rounded-xl flex items-center justify-center mx-auto mb-2" data-id="element-377">
                <GiftIcon className="w-4 h-4 text-[#C8A84E]" data-id="element-378" />
              </div>
              <p className="text-[10px] md:text-xs text-[#666666] mb-1" data-id="element-379">
                Giá trị điểm quy đổi
              </p>
              <div className="text-lg md:text-xl font-bold text-[#22C55E]" data-id="element-380">
                +<AnimatedCompactNumber value={result.totalPointsCreditValue} data-id="element-381" />
              </div>
              <p className="text-[9px] text-[#666666] mt-1" data-id="element-382">
                1.000 điểm = 500.000đ tín dụng
              </p>
            </Card>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          y: 16
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: 0.2
        }} data-id="element-383">
            <Card className="text-center !p-4 md:!p-5" data-id="element-384">
              <div className="w-9 h-9 rounded-xl flex items-center justify-center mx-auto mb-2" style={{
              backgroundColor: `${result.finalTier.color}15`
            }} data-id="element-385">
                <span className="text-base" data-id="element-386">{result.finalTier.icon}</span>
              </div>
              <p className="text-[10px] md:text-xs text-[#666666] mb-1" data-id="element-387">
                Hạng đạt được
              </p>
              <p className="text-lg md:text-xl font-bold" style={{
              color: result.finalTier.color
            }} data-id="element-388">
                {result.finalTier.nameVi}
              </p>
              {result.finalTier.pointMultiplier > 1 && <p className="text-[10px] text-[#B0B0B0] mt-0.5" data-id="element-389">
                  Tích điểm x{result.finalTier.pointMultiplier}
                </p>}
            </Card>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          y: 16
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: 0.25
        }} data-id="element-390">
            <Card className="text-center !p-4 md:!p-5" data-id="element-391">
              <div className="w-9 h-9 bg-[#C8A84E]/10 rounded-xl flex items-center justify-center mx-auto mb-2" data-id="element-392">
                <StarIcon className="w-4 h-4 text-[#C8A84E]" data-id="element-393" />
              </div>
              <p className="text-[10px] md:text-xs text-[#666666] mb-1" data-id="element-394">
                Điểm tích lũy
              </p>
              <div className="text-lg md:text-xl font-bold text-[#C8A84E]" data-id="element-395">
                <AnimatedPoints value={result.totalPoints} data-id="element-396" />{' '}
                <span className="text-xs font-normal text-[#666666]" data-id="element-397">điểm</span>
              </div>
            </Card>
          </motion.div>
        </div>

        {/* POINTS VALUE CONVERSION BREAKDOWN */}
        <motion.div initial={{
        opacity: 0,
        y: 16
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        delay: 0.15
      }} className="mb-6" data-id="element-398">
          <Card className="!p-5 md:!p-6" data-id="element-399">
            <div className="flex items-center gap-2 mb-4" data-id="element-400">
              <StarIcon className="w-5 h-5 text-[#C8A84E]" data-id="element-401" />
              <h3 className="text-sm font-semibold text-white" data-id="element-402">
                Quy Đổi Điểm → Tín Dụng Mua Hàng
              </h3>
            </div>

            {/* How points are earned */}
            <div className="bg-[#141414] rounded-lg p-4 mb-4" data-id="element-403">
              <p className="text-xs text-[#666666] uppercase tracking-wider mb-2 font-medium" data-id="element-404">
                Cách tích điểm
              </p>
              <div className="flex items-center gap-3" data-id="element-405">
                <div className="flex items-center gap-2 flex-1" data-id="element-406">
                  <span className="text-sm text-white font-semibold" data-id="element-407">
                    100.000đ
                  </span>
                  <span className="text-xs text-[#666666]" data-id="element-408">chi tiêu</span>
                </div>
                <ArrowRightIcon className="w-4 h-4 text-[#C8A84E]" data-id="element-409" />
                <div className="flex items-center gap-2 flex-1 justify-end" data-id="element-410">
                  <span className="text-sm text-[#C8A84E] font-semibold" data-id="element-411">
                    1 điểm
                  </span>
                </div>
              </div>
              {result.finalTier.pointMultiplier > 1 && <p className="text-[10px] text-[#B0B0B0] mt-1.5" data-id="element-412">
                  Hạng {result.finalTier.nameVi} của bạn: 100.000đ ={' '}
                  <span className="text-[#C8A84E] font-semibold" data-id="element-413">
                    {result.finalTier.pointMultiplier} điểm
                  </span>
                </p>}
            </div>

            {/* Conversion table */}
            <div className="bg-[#141414] rounded-lg overflow-hidden" data-id="element-414">
              <div className="grid grid-cols-3 gap-px bg-[#2A2A2A]" data-id="element-415">
                {/* Header */}
                <div className="bg-[#1A1A1A] px-3 py-2" data-id="element-416">
                  <p className="text-[10px] text-[#666666] uppercase tracking-wider font-medium" data-id="element-417">
                    Điểm
                  </p>
                </div>
                <div className="bg-[#1A1A1A] px-3 py-2" data-id="element-418">
                  <p className="text-[10px] text-[#666666] uppercase tracking-wider font-medium" data-id="element-419">
                    Tín dụng nhận
                  </p>
                </div>
                <div className="bg-[#1A1A1A] px-3 py-2" data-id="element-420">
                  <p className="text-[10px] text-[#666666] uppercase tracking-wider font-medium" data-id="element-421">
                    Chi tiêu cần
                  </p>
                </div>

                {/* Rows */}
                {[{
                points: 100,
                credits: 50000,
                spend: 10000000
              }, {
                points: 500,
                credits: 250000,
                spend: 50000000
              }, {
                points: 1000,
                credits: 500000,
                spend: 100000000
              }, {
                points: 2000,
                credits: 1000000,
                spend: 200000000
              }, {
                points: 5000,
                credits: 2500000,
                spend: 500000000
              }].map((row, i) => <Fragment key={i} data-id="element-422">
                    <div className="bg-[#141414] px-3 py-2.5" data-id="element-423">
                      <p className="text-sm text-[#C8A84E] font-semibold" data-id="element-424">
                        {row.points.toLocaleString('vi-VN')}
                      </p>
                    </div>
                    <div className="bg-[#141414] px-3 py-2.5" data-id="element-425">
                      <p className="text-sm text-[#22C55E] font-semibold" data-id="element-426">
                        {formatVND(row.credits)}
                      </p>
                    </div>
                    <div className="bg-[#141414] px-3 py-2.5" data-id="element-427">
                      <p className="text-sm text-[#B0B0B0]" data-id="element-428">
                        {formatVNDCompact(row.spend)}
                      </p>
                    </div>
                  </Fragment>)}
              </div>
            </div>

            {/* Your personalized example */}
            <div className="mt-4 bg-[#C8A84E]/5 border border-[#C8A84E]/15 rounded-lg p-3" data-id="element-429">
              <p className="text-xs text-[#C8A84E] font-medium mb-1" data-id="element-430">
                📊 Với chi tiêu của bạn ({formatVNDCompact(monthlySpend)}
                /tháng):
              </p>
              <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-[#B0B0B0]" data-id="element-431">
                <span data-id="element-432">
                  Mỗi tháng tích:{' '}
                  <span className="text-[#C8A84E] font-semibold" data-id="element-433">
                    {result.months[0]?.pointsEarned.toLocaleString('vi-VN') || 0}{' '}
                    điểm
                  </span>
                </span>
                <span data-id="element-434">
                  Giá trị:{' '}
                  <span className="text-[#22C55E] font-semibold" data-id="element-435">
                    {formatVND(result.months[0]?.pointsCreditValue || 0)}
                  </span>
                </span>
                <span data-id="element-436">
                  Sau 12 tháng:{' '}
                  <span className="text-[#C8A84E] font-semibold" data-id="element-437">
                    {result.totalPoints.toLocaleString('vi-VN')} điểm
                  </span>{' '}
                  ={' '}
                  <span className="text-[#22C55E] font-semibold" data-id="element-438">
                    {formatVND(result.totalPointsCreditValue)}
                  </span>
                </span>
              </div>
            </div>
          </Card>
        </motion.div>

        {/* CUMULATIVE GROWTH CHART */}
        <motion.div initial={{
        opacity: 0,
        y: 16
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        delay: 0.15
      }} className="mb-6" data-id="element-439">
          <Card className="!p-5 md:!p-6" data-id="element-440">
            <div className="flex items-center justify-between mb-4" data-id="element-441">
              <div className="flex items-center gap-2" data-id="element-442">
                <TrendingUpIcon className="w-5 h-5 text-[#22C55E]" data-id="element-443" />
                <h3 className="text-sm font-semibold text-white" data-id="element-444">
                  Tăng Trưởng Tích Lũy
                </h3>
              </div>
              <span className="text-xs text-[#22C55E] font-semibold bg-[#22C55E]/10 px-2 py-0.5 rounded-full" data-id="element-445">
                +{formatVNDCompact(result.totalSavings)} / 12 tháng
              </span>
            </div>
            <CumulativeChart months={result.months} tierUpgrades={result.tierUpgrades} data-id="element-446" />
          </Card>
        </motion.div>

        {/* STACKED BAR CHART */}
        <motion.div initial={{
        opacity: 0,
        y: 16
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        delay: 0.2
      }} className="mb-8" data-id="element-447">
          <Card className="!p-5 md:!p-6" data-id="element-448">
            <div className="flex items-center gap-2 mb-4" data-id="element-449">
              <ZapIcon className="w-5 h-5 text-[#C8A84E]" data-id="element-450" />
              <h3 className="text-sm font-semibold text-white" data-id="element-451">
                Chi Tiết Theo Tháng
              </h3>
            </div>
            <StackedBarChart months={result.months} data-id="element-452" />
          </Card>
        </motion.div>

        {/* Tier Progression Timeline */}
        <motion.div initial={{
        opacity: 0,
        y: 16
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        delay: 0.2
      }} data-id="element-453">
          <Card className="!p-6" data-id="element-454">
            <div className="flex items-center gap-2 mb-5" data-id="element-455">
              <TrendingUpIcon className="w-5 h-5 text-[#C8A84E]" data-id="element-456" />
              <h3 className="text-sm font-semibold text-white" data-id="element-457">
                Lộ Trình Nâng Hạng Trong 12 Tháng
              </h3>
            </div>
            <div className="relative" data-id="element-458">
              <div className="h-2 bg-[#2A2A2A] rounded-full relative overflow-visible" data-id="element-459">
                <motion.div className="absolute inset-y-0 left-0 rounded-full" style={{
                background: `linear-gradient(to right, #666666, ${result.finalTier.color})`
              }} initial={{
                width: 0
              }} whileInView={{
                width: '100%'
              }} viewport={{
                once: true
              }} transition={{
                duration: 1.2,
                ease: 'easeOut'
              }} data-id="element-460" />
              </div>
              <div className="relative mt-4" data-id="element-461">
                <div className="flex justify-between" data-id="element-462">
                  {TIER_CONFIGS.map((tier, i) => {
                  const reached = result.months[11] ? result.months[11].cumulativePoints >= tier.minPoints : false;
                  const upgradeInfo = result.tierUpgrades.find(u => u.tier.level === tier.level);
                  const reachedMonth = tier.level === 'member' ? 1 : upgradeInfo?.month;
                  return <div key={tier.level} className="flex flex-col items-center" style={{
                    width: '25%'
                  }} data-id="element-463">
                        <motion.div className={`w-10 h-10 rounded-full flex items-center justify-center text-lg border-2 transition-all ${reached ? 'border-transparent shadow-lg' : 'border-[#2A2A2A] opacity-40'}`} style={{
                      backgroundColor: reached ? `${tier.color}20` : '#141414',
                      boxShadow: reached ? `0 0 16px ${tier.color}30` : 'none'
                    }} initial={{
                      scale: 0.8,
                      opacity: 0
                    }} whileInView={{
                      scale: reached ? 1 : 0.85,
                      opacity: reached ? 1 : 0.4
                    }} viewport={{
                      once: true
                    }} transition={{
                      delay: 0.3 + i * 0.15,
                      type: 'spring'
                    }} data-id="element-464">
                          {tier.icon}
                        </motion.div>
                        <motion.div className="mt-2 text-center" initial={{
                      opacity: 0
                    }} whileInView={{
                      opacity: 1
                    }} viewport={{
                      once: true
                    }} transition={{
                      delay: 0.4 + i * 0.15
                    }} data-id="element-465">
                          <p className={`text-xs font-semibold ${reached ? 'text-white' : 'text-[#666666]'}`} data-id="element-466">
                            {tier.nameVi}
                          </p>
                          {reached && reachedMonth && <p className="text-[10px] mt-0.5" style={{
                        color: tier.color
                      }} data-id="element-467">
                              {reachedMonth === 1 ? 'Bắt đầu' : `Tháng ${reachedMonth}`}
                            </p>}
                          {reached && tier.pointMultiplier > 1 && <p className="text-[10px] text-[#C8A84E]" data-id="element-468">
                              x{tier.pointMultiplier} điểm
                            </p>}
                          {!reached && <p className="text-[10px] text-[#666666]" data-id="element-469">
                              {tier.minPoints.toLocaleString('vi-VN')} điểm
                            </p>}
                        </motion.div>
                      </div>;
                })}
                </div>
              </div>
            </div>
          </Card>
        </motion.div>

        {/* B2B Value Props */}
        <motion.div initial={{
        opacity: 0,
        y: 16
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        delay: 0.1
      }} className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-470">
          <div className="bg-[#1A1A1A] border border-[#2A2A2A] rounded-xl p-5" data-id="element-471">
            <div className="w-10 h-10 bg-[#22C55E]/10 rounded-xl flex items-center justify-center mb-3" data-id="element-472">
              <ZapIcon className="w-5 h-5 text-[#22C55E]" data-id="element-473" />
            </div>
            <h4 className="text-sm font-semibold text-white mb-1" data-id="element-474">
              +2.5% Tín Dụng Tức Thì
            </h4>
            <p className="text-xs text-[#B0B0B0] leading-relaxed" data-id="element-475">
              Nhận ngay +2.5% tín dụng thưởng mỗi lần nạp. Dùng ngay cho đơn
              hàng tiếp theo.
            </p>
          </div>
          <div className="bg-[#1A1A1A] border border-[#2A2A2A] rounded-xl p-5" data-id="element-476">
            <div className="w-10 h-10 bg-[#C8A84E]/10 rounded-xl flex items-center justify-center mb-3" data-id="element-477">
              <TrendingUpIcon className="w-5 h-5 text-[#C8A84E]" data-id="element-478" />
            </div>
            <h4 className="text-sm font-semibold text-white mb-1" data-id="element-479">
              Điểm Đổi Tín Dụng
            </h4>
            <p className="text-xs text-[#B0B0B0] leading-relaxed" data-id="element-480">
              Tích điểm mỗi đơn hàng, đổi thành tín dụng mua hàng. Hạng cao =
              tích điểm nhanh x2.
            </p>
          </div>
          <div className="bg-[#1A1A1A] border border-[#2A2A2A] rounded-xl p-5" data-id="element-481">
            <div className="w-10 h-10 bg-[#3B82F6]/10 rounded-xl flex items-center justify-center mb-3" data-id="element-482">
              <ShieldCheckIcon className="w-5 h-5 text-[#3B82F6]" data-id="element-483" />
            </div>
            <h4 className="text-sm font-semibold text-white mb-1" data-id="element-484">
              Minh Bạch & Linh Hoạt
            </h4>
            <p className="text-xs text-[#B0B0B0] leading-relaxed" data-id="element-485">
              Theo dõi mọi giao dịch real-time. Tín dụng ví sử dụng linh hoạt,
              không ràng buộc.
            </p>
          </div>
        </motion.div>
      </div>
    </section>;
}